/**
 * Centralized i18n configuration.
 *
 * - Default: French (fr).
 * - Supported: French (fr), English (en).
 * - Language is detected in this order: localStorage('lang') → fallback (fr).
 *   Browser language is intentionally NOT in the detection order to enforce FR as the true default.
 * - Add new keys to both `locales/fr.json` and `locales/en.json`.
 * - Components consume translations via `const { t } = useTranslation()`.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import fr from './locales/fr.json';
import en from './locales/en.json';

export const SUPPORTED_LANGUAGES = [
    { code: 'fr', label: 'Français' },
    { code: 'en', label: 'English' },
];

// Keeps <title>, meta description and <html lang> in sync with the active language.
const applyDocumentMeta = () => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = i18n.resolvedLanguage || 'fr';
    document.title = i18n.t('app.document_title');
    document.querySelector('meta[name="description"]')?.setAttribute('content', i18n.t('app.document_description'));
};
i18n.on('languageChanged', applyDocumentMeta);

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: {
            fr: { translation: fr },
            en: { translation: en },
        },
        fallbackLng: 'fr',
        supportedLngs: ['fr', 'en'],
        detection: {
            order: ['localStorage'],
            caches: ['localStorage'],
            lookupLocalStorage: 'lang',
        },
        interpolation: { escapeValue: false },
        returnNull: false,
    });

export default i18n;
